import { TaskUtility } from './../task.utility';
import { Component, OnInit, Input } from '@angular/core';
import { GridDataResult, DataStateChangeEvent, RowClassArgs } from '@progress/kendo-angular-grid';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { State } from '@progress/kendo-data-query';
import { TaskListService } from 'src/app/core/service/grid-data/grid-data-task.service';
import moment from 'moment';
import { TextProcessor } from '../../core/utility/text.processor';
import { LanguageService } from 'src/app/core/service/language.service';
import { IntlService } from '@progress/kendo-angular-intl';

@Component({
  standalone: false,
  selector: 'app-task-list-widget',
  templateUrl: './task-list-widget.component.html',
  styleUrls: ['./task-list-widget.component.scss']
})
export class TaskListWidgetComponent implements OnInit
{
  @Input() public memberId: number;

  public pageSizes = [5, 10, 25, 50, 100];
  public taskFilters: Array<{ text: string; value: string }>;
  public selectedTaskFilter = 'OPEN';
  public gridView: GridDataResult;
  public count: number;
  public task = null;
  public assigneeDialogOpened = false;
  public taskDueDateOpened = false;
  public shortTaskName: string;
  public distinctPriorities: Array<{ priorityName: string; priority: string }>;
  public windowState = 'default';

  /**
   * Loading indicator
   */
  public loading = false;

  /**
   * Controls the Task detail modal
   */
  public taskOpened = false;

  /**
   * Calendar texts to use with moment translations
   */
  public calendarTexts: Array<{ text: string }> = [];

  /**
   * Task list Kendo grid state.
   */
  public state: State = {
    skip: 0,
    // Initial filter descriptor
    filter: {
      logic: 'and',
      filters: []
    }
  };

  constructor(
    private taskUtility: TaskUtility,
    private appConfigService: AppConfigService,
    private taskListService: TaskListService,
    private languageService: LanguageService,
    public intl: IntlService)
  {
  }

  public ngOnInit(): void
  {
    this.taskFilters = this.taskUtility.taskFilters;
    this.calendarTexts = this.taskUtility.calendarTexts;
    this.distinctPriorities = this.taskUtility.distinctPriorities;
    this.getTasks();
  }

  /**
   * Task filter has been selected.
   *
   * @param filter Filter: Open, Completed, All
   */
  public taskFilterSelected(filter: string): void
  {
    this.selectedTaskFilter = filter;
    this.state.skip = 0;
    this.getTasks();
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.state = state;
    this.getTasks();
  }

  public rowCallback(context: RowClassArgs): any
  {
    return {
      low: context.dataItem.priority === 'Low',
      medium: context.dataItem.priority === 'Medium',
      high: context.dataItem.priority === 'High',
      urgent: context.dataItem.priority === 'Urgent'
    };
  }

  /**
   * Sets the selected task, and opens the task assignment dialog modal.
   *
   * @param task Selected task for re-assignment.
   */
  public openTaskAssigneeDialog(task): void
  {
    this.task = task;
    this.shortTaskName = TextProcessor.truncateText(this.task.name, 35);
    this.assigneeDialogOpened = true;
  }

  /**
   * Sets the selected task to null, and closes the task assignment dialog modal.
   */
  public closeTaskAssigneeDialog(): void
  {
    this.task = null;
    this.assigneeDialogOpened = false;
  }

  /**
   * Sets the selected task to null, and closes the task assignment dialog modal.
   */
  public closeTaskDueDateDialog(): void
  {
    this.task = null;
    this.taskDueDateOpened = false;
  }

  /**
   * Updates a task's assignee.
   *
   * @param task Selected task for re-assignment.
   * @param assigneeForm Form of the task-assign component.
   */
  public updateTaskAssignee(assignee): void
  {
    this.task.assigneeName = assignee.name;
    this.task.assignee = assignee.username;
    this.closeTaskAssigneeDialog();
  }

  /**
   * Opens the task detail modal.
   *
   * @param task Task object
   */
  public openTask(task: any): void
  {
    this.task = task;
    this.task.name = TextProcessor.truncateText(this.task.name, 150);
    this.task.inMemberDetail = true;
    this.taskOpened = true;
    document.body.style.overflow = 'hidden';
  }

  public closeTaskDialog(): void
  {
    this.taskOpened = false;
    document.body.style.overflow = '';
  }

  /**
   * Event handler for task completion.
   *
   * @param taskId Task ID
   */
  public onTaskCompleted(): void
  {
    this.closeTaskDialog();
    this.getTasks();
  }

  /**
   * Sets the selected task, and opens the task assignment dialog modal.
   *
   * @param task Selected task for re-assignment.
   */
  public openTaskDueDateDialog(task): void
  {
    this.task = task;
    this.shortTaskName = TextProcessor.truncateText(this.task.name, 35);
    this.taskDueDateOpened = true;
  }

  /**
   * Updates a task's due date.
   *
   * @param task Selected task for re-assignment.
   * @param assigneeForm Form of the task-assign component.
   */
  public updateTaskDueDate(task): void
  {
    this.task.dueDate = task.dueDate;
    this.setDueTaskText(task);
    this.taskDueDateOpened = false;
  }

  public dueDateUpdated(task: any): void
  {
    this.task.dueDate = task.dueDate;
    this.setDueTaskText(task);
  }

  public getDialogTaskDetailsWidth(): any
  {
    return '1300px';
  }

  private getTasks(): void
  {
    this.state = {
      filter: {
        filters: [
          { field: 'memberId', operator: 'eq', value: this.memberId, logic: 'or' },
          { field: 'memberIdFromVar', operator: 'eq', value: this.memberId, logic: 'or' },
        ], logic: 'and'
      },
      group: [],
      skip: this.state.skip,
      sort: this.state.sort !== undefined ? this.state.sort : [{ field: 'dueDate', dir: 'asc' }],
      take: this.appConfigService.getConfig().pageSize
    };

    if (this.selectedTaskFilter === 'OPEN')
    {
      this.state.filter.filters.push({ field: 'closedOn', operator: 'isnull', value: '', logic: 'and' });
    }
    else if (this.selectedTaskFilter === 'COMPLETED')
    {
      this.state.filter.filters.push({ field: 'closedOn', operator: 'isnotnull', value: '', logic: 'and' });
    }

    this.taskListService.queryByState(this.state, (task: any) =>
    {
      task.data.forEach((t: any) =>
      {
        if (t.dueDate !== null)
        {
          t.dueDate = new Date(t.dueDate);
        }
        t.openedOn = new Date(t.openedOn);
        this.setDueTaskText(t);

        t.priorityBadgeClass = this.taskUtility.priorityBadgeClass(t.priority);
        t.priority = this.taskUtility.priority(t.priority);
      });

      this.count = task.total;
      this.gridView = {
        data: task.data,
        total: task.total
      };
    });
  }

  private setDueTaskText(task): void
  {
    const lang = this.languageService.currentLanguage;
    task.nextTaskDueDateText = !task.dueDate ? '' : moment(task.dueDate).calendar(moment(), {
      sameDay: `[${this.calendarTexts[0].text}]`,
      nextDay: `[${this.calendarTexts[1].text}]`,
      nextWeek: 'dddd',
      lastDay: `[${this.calendarTexts[2].text}]`,
      lastWeek: lang === 'en' ? `[${this.calendarTexts[3].text}] dddd` : `dddd [${this.calendarTexts[3].text}]`,
      sameElse: 'M/D/YYYY'
    });
  }
}
